import { StatsObserver } from '../../stats/observer';
import { GtmStatsTracker } from './tracker';
import { StatsDataPropertyInterface } from '../../stats/data/property.interface';
import { StatsContextInterface } from '../../stats/context.interface';
import { GtmStatsAdapterProperty } from './adapter/property';
import { GtmStatsAdapterEntityPage } from './adapter/entity-page';
import { GtmStatsAdapterEntityLeadClick } from './adapter/entity-lead-click';
import { GtmStatsAdapterEntityLeadSend } from './adapter/entity-lead-send';
import { StatsDataAgentInterface } from '../../stats/data/agent.interface';
import { GtmStatsAdapterAgent } from './adapter/agent';
import { StatsDataBrokerInterface } from '../../stats/data/broker.interface';
import { GtmStatsAdapterBroker } from './adapter/broker';
import { GtmStatsAdapterEntityImpression } from './adapter/entity-impression';
import { GtmStatsAdapterEntityClick } from './adapter/entity-click';
import { GtmStatsAdapterEventCtaClick } from './adapter/event-cta-click';
import { GtmStatsAdapterEventCustomCtaClick } from './adapter/event-custom-cta-click';
import { GtmStatsAdapterEventErrorPage } from './adapter/event-error-page';
import { GtmStatsAdapterEventBrokerSearch } from './adapter/event-broker-search';
import { GtmStatsAdapterEventAgentSearch } from './adapter/event-agent-search';
import { GtmStatsAdapterEcommerceAddToCart } from './adapter/ecommerce-add-to-cart';
import { GtmStatsAdapterEcommerceRemoveFromCart } from './adapter/ecommerce-remove-from-cart';
import { StatsEmitterEvent } from '../../stats/emitter.event';
import { GtmStatsAdapterEcommerceCheckout } from './adapter/ecommerce-checkout';
import { GtmStatsAdapterVariables } from './adapter/variables';
import { StatsDataUserInterface } from '../../stats/data/user.interface';
import { GtmStatsAdapterEventUserSignIn } from './adapter/event-user-sign-in';
import { GtmStatsAdapterEventUserSignOut } from './adapter/event-user-sign-out';
import { GtmStatsAdapterEventUserRegister } from './adapter/event-user-register';
import { GtmStatsAdapterEventUser } from './adapter/event-user';

export class GtmStatsObserver extends StatsObserver {
  /**
   *
   * @param statsTracker
   */
  constructor(protected statsTracker: GtmStatsTracker) {
    super(statsTracker);
  }

  /**
   * @inheritDoc
   */
  protected onPageView(context: StatsContextInterface): void {
    // Generate variables
    const data = new GtmStatsAdapterVariables(context).getData();

    // Track converted data
    this.statsTracker.pageView(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertyPage(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterProperty(rawData, context, StatsEmitterEvent.propertyPage).getData();
    const data = new GtmStatsAdapterEntityPage(entity, context).getData();

    // Track converted data
    this.statsTracker.propertyPage(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertyImpression(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterProperty(rawData, context, StatsEmitterEvent.propertyImpression).getData();
    const data = new GtmStatsAdapterEntityImpression(entity, context).getData();

    // Track converted data
    this.statsTracker.propertyImpression(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertyLeadClick(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterProperty(rawData, context, StatsEmitterEvent.propertyLeadClick).getData();
    const data = new GtmStatsAdapterEntityLeadClick(entity, context).getData();

    // Track converted data
    this.statsTracker.propertyLeadClick(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertyLeadSend(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterProperty(rawData, context, StatsEmitterEvent.propertyLeadSend).getData();
    const data = new GtmStatsAdapterEntityLeadSend(entity, context).getData();

    // Track converted data
    this.statsTracker.propertyLeadSend(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertyClick(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterProperty(rawData, context, StatsEmitterEvent.propertyClick).getData();
    const data = new GtmStatsAdapterEntityClick(entity, context).getData();

    // Track converted data
    this.statsTracker.propertyClick(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertySave(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert data
    const property = new GtmStatsAdapterProperty(rawData, context, StatsEmitterEvent.propertySave).getData();
    const data = new GtmStatsAdapterEcommerceAddToCart(property, context).getData();

    // Track converted data
    this.statsTracker.propertySave(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertyUnsave(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert data
    const property = new GtmStatsAdapterProperty(rawData, context, StatsEmitterEvent.propertyUnsave).getData();
    const data = new GtmStatsAdapterEcommerceRemoveFromCart(property, context).getData();

    // Track converted data
    this.statsTracker.propertyUnsave(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertyImageBrowse(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert data
    const property = new GtmStatsAdapterProperty(rawData, context, StatsEmitterEvent.propertyImageBrowse).getData();
    const data = new GtmStatsAdapterEcommerceCheckout(property, context).getData();

    // Track converted data
    this.statsTracker.propertyImageBrowse(data);
  }

  /**
   * @inheritDoc
   */
  protected onAgentPage(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterAgent(rawData, context).getData();
    const data = new GtmStatsAdapterEntityPage(entity, context).getData();

    // Track converted data
    this.statsTracker.agentPage(data);
  }

  /**
   * @inheritDoc
   */
  protected onAgentSerp(context: StatsContextInterface): void {
    // Convert data
    const data = [new GtmStatsAdapterEventAgentSearch(context).getData()];

    // Track converted data
    this.statsTracker.agentSerp(data);
  }

  /**
   * @inheritDoc
   */
  protected onAgentImpression(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterAgent(rawData, context).getData();
    const data = new GtmStatsAdapterEntityImpression(entity, context).getData();

    // Track converted data
    this.statsTracker.agentImpression(data);
  }

  /**
   * @inheritDoc
   */
  protected onAgentLeadClick(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterAgent(rawData, context).getData();
    const data = new GtmStatsAdapterEntityLeadClick(entity, context).getData();

    // Track converted data
    this.statsTracker.agentLeadClick(data);
  }

  /**
   * @inheritDoc
   */
  protected onAgentLeadSend(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterAgent(rawData, context).getData();
    const data = new GtmStatsAdapterEntityLeadSend(entity, context).getData();

    // Track converted data
    this.statsTracker.agentLeadSend(data);
  }

  /**
   * @inheritDoc
   */
  protected onAgentClick(rawData: StatsDataAgentInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterAgent(rawData, context).getData();
    const data = new GtmStatsAdapterEntityClick(entity, context).getData();

    // Track converted data
    this.statsTracker.agentClick(data);
  }

  /**
   * @inheritDoc
   */
  protected onBrokerPage(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterBroker(rawData, context).getData();
    const data = new GtmStatsAdapterEntityPage(entity, context).getData();

    // Track converted data
    this.statsTracker.brokerPage(data);
  }

  /**
   * @inheritDoc
   */
  protected onBrokerSerp(context: StatsContextInterface): void {
    // Convert data
    const data = [new GtmStatsAdapterEventBrokerSearch(context).getData()];

    // Track converted data
    this.statsTracker.brokerSerp(data);
  }

  /**
   * @inheritDoc
   */
  protected onBrokerImpression(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterBroker(rawData, context).getData();
    const data = new GtmStatsAdapterEntityImpression(entity, context).getData();

    // Track converted data
    this.statsTracker.brokerImpression(data);
  }

  /**
   * @inheritDoc
   */
  protected onBrokerLeadClick(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterBroker(rawData, context).getData();
    const data = new GtmStatsAdapterEntityLeadClick(entity, context).getData();

    // Track converted data
    this.statsTracker.brokerLeadClick(data);
  }

  /**
   * @inheritDoc
   */
  protected onBrokerLeadSend(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterBroker(rawData, context).getData();
    const data = new GtmStatsAdapterEntityLeadSend(entity, context).getData();

    // Track converted data
    this.statsTracker.brokerLeadSend(data);
  }

  /**
   * @inheritDoc
   */
  protected onBrokerClick(rawData: StatsDataBrokerInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterBroker(rawData, context).getData();
    const data = new GtmStatsAdapterEntityClick(entity, context).getData();

    // Track converted data
    this.statsTracker.brokerClick(data);
  }

  /**
   * Emit a user sign in
   *
   * @param rawData
   * @param context
   */
  protected onUserSignIn(rawData: StatsDataUserInterface, context: StatsContextInterface): void {
    // Convert data
    const eventUser = new GtmStatsAdapterEventUserSignIn(context).getData();
    const data = new GtmStatsAdapterEventUser(eventUser, context).getData();

    // Track converted data
    this.statsTracker.userSignIn(data);
  }

  /**
   * Emit a user sign out
   *
   * @param rawData
   * @param context
   */
  protected onUserSignOut(rawData: StatsDataUserInterface, context: StatsContextInterface): void {
    // Convert data
    const eventUser = new GtmStatsAdapterEventUserSignOut(context).getData();
    const data = new GtmStatsAdapterEventUser(eventUser, context).getData();

    // Track converted data
    this.statsTracker.userSignOut(data);
  }

  /**
   * Emit a user register
   *
   * @param rawData
   * @param context
   */
  protected onUserRegister(rawData: StatsDataUserInterface, context: StatsContextInterface): void {
    // Convert data
    const eventUser = new GtmStatsAdapterEventUserRegister(context).getData();
    const data = new GtmStatsAdapterEventUser(eventUser, context).getData();

    // Track converted data
    this.statsTracker.userRegister(data);
  }

  /**
   * @inheritDoc
   */
  protected onCtaClick(context: StatsContextInterface): void {
    let data;

    // Check if custom event is defined to decide which adapter to use
    if (context.local.cta.customEvent) {
      data = new GtmStatsAdapterEventCustomCtaClick(context).getData();
    } else {
      data = new GtmStatsAdapterEventCtaClick(context).getData();
    }

    this.statsTracker.ctaClick(data);
  }

  /**
   * @inheritDoc
   */
  protected onErrorPage(context: StatsContextInterface): void {
    const data = [new GtmStatsAdapterEventErrorPage(context).getData()];

    this.statsTracker.errorPage(data);
  }

  /**
   * @inheritDoc
   */
  protected onMyAccountPage(context: StatsContextInterface): void {
    // Generate variables
    const data = new GtmStatsAdapterVariables(context).getData();

    // Track converted data
    this.statsTracker.pageView(data);
  }

  /**
   * @inheritDoc
   */
  protected onBuyVsRentCalculatorPage(context: StatsContextInterface): void {
    // Generate variables
    const data = new GtmStatsAdapterVariables(context).getData();

    // Track converted data
    this.statsTracker.buyVsRentCalculatorPage(data);
  }

  /**
   * @inheritDoc
   */
  protected onPropertyLeadClickWhatsapp(rawData: StatsDataPropertyInterface, context: StatsContextInterface): void {
    // Convert data
    const entity = new GtmStatsAdapterProperty(rawData, context, StatsEmitterEvent.propertyLeadClickWhatsapp).getData();
    const data = new GtmStatsAdapterEntityLeadClick(entity, context).getData();

    // Track converted data
    this.statsTracker.propertyLeadClickWhatsapp(data);
  }
}
